/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useStore, ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import PropTypes from 'prop-types';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { Button, Link } from '@one-thd/sui-atomic-components';
import THDStorageUtils, { COOKIE_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { podFulFillmentUtils, resolveSelectedFulfillmentMethod } from '@thd-olt-component-react/fulfillment';
import { getFulfillmentLocation } from '../utils';
import { AddToQuoteDrawer } from './drawers/AddToQuoteDrawer';
import { useQuoteProduct } from './hooks/useQuotes';
import { dataModel } from '../dataModels/dataModel';
import { useCustomerInformation } from './hooks/useCustomerInformation';

const AddToQuote = ({
  itemId = '',
  quantity = 0,
  fulfillmentLocation = '',
  fulfillmentMethod = '',
  disabled = null,
  products = [],
  productExceptions = [],
  buttonRef = null,
  buttonText = null,
  projectQuoteData = {},
  quoteStartTime = null,
  renderAsLink = false,
  applianceDeliveryStore = '',
  enableApplianceAddToQuote = false,
}) => {
  const { deliveryZip: deliveryZipCode, channel } = useContext(ExperienceContext);
  const projectZipCode = projectQuoteData?.projectZipCode;
  const isMobile = channel === 'mobile';
  const store = useStore();
  const [drawerShowing, setDrawerShowing] = useState(false);
  const quoteTimeTrackingRef = useRef({ startTime: null });
  const zipCode = projectZipCode ?? deliveryZipCode;
  const enableNewATQUIFS = useConfigService('fs:showNewATQUI') || false;
  const showNewATQUI = enableNewATQUIFS || typeof document !== 'undefined'
    && THDStorageUtils.get(COOKIE_STORAGE, { key: 'showNewATQUI' }) === 'true';

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-quote.ready');
  }, []);

  const customerInfo = useCustomerInformation();

  let item = null;
  if (itemId || products.length === 1) {
    item = {
      itemId: itemId || products[0]?.itemId,
      quantity: quantity || products[0]?.quantity
    };
  }

  const { quoteProduct } = useQuoteProduct({ itemId: item?.itemId, storeId: store?.storeId });
  const product = quoteProduct?.product || {};

  let isDisabled;
  if (disabled !== null) {
    isDisabled = disabled;
  } else if (product && Object.keys(product).length > 0) {
    isDisabled = !podFulFillmentUtils.isATCEnabled(product) || (!enableApplianceAddToQuote && podFulFillmentUtils.isAppliance(product));
  } else if (products?.length > 1) {
    isDisabled = products.some((currentProd) => {
      return (
        !currentProd || !podFulFillmentUtils.isATCEnabled(currentProd) || (!enableApplianceAddToQuote && podFulFillmentUtils.isAppliance(currentProd))
      );
    });
  } else {
    isDisabled = true;
  }

  // A length-1 `products` array is routed through the single-item branch above (see `item`),
  // where the shopper's selection lives on products[0] rather than on the top-level props.
  // Resolve it the same way the multi-product branch does so a single selected item isn't
  // silently reverted to the auto-pick. With no selection the resolver returns the plain
  // auto-pick, so existing single-item callers (PIP, etc.) are unchanged.
  const singleItemSelection = products.length === 1 ? products[0] : null;
  const fulfillmentMethodFromFulfillmentComponent = resolveSelectedFulfillmentMethod
    ? resolveSelectedFulfillmentMethod(product, {
      selectedMethod: singleItemSelection?.selectedFulfillmentMethod,
      bulkPreference: singleItemSelection?.bulkPreference,
      storeId: store?.storeId
    })
    : podFulFillmentUtils.getFulfillmentMethod(product);
  const fulfillmentLocationFromProductPodUtils = getFulfillmentLocation({
    fulfillmentMethod: fulfillmentMethodFromFulfillmentComponent,
    store,
    product,
    deliveryZip: zipCode
  });

  const items = item
    ? [
      {
        ...item,
        fulfillmentMethod: fulfillmentMethod || fulfillmentMethodFromFulfillmentComponent,
        fulfillmentLocation: fulfillmentLocation || fulfillmentLocationFromProductPodUtils,
        identifiers: product?.identifiers,
        pricing: product?.pricing,
        media: product?.media,
        info: product?.info,
        taxonomy: product?.taxonomy,
      }
    ]
    : products.map((currentProduct) => {
      // Resolve fulfillment the same way the cart does: honor the shopper's per-item radio
      // selection or bulk pickup/delivery preference (threaded from the Projects materials
      // list as selectedFulfillmentMethod/bulkPreference), else fall back to the auto-pick.
      // Resolving here — against currentProduct's full fulfillment data — keeps quote in
      // parity with cart. Guarded so an older fulfillment package (no resolver) still works.
      const resolvedFulfillmentMethod = resolveSelectedFulfillmentMethod
        ? resolveSelectedFulfillmentMethod(currentProduct, {
          selectedMethod: currentProduct?.selectedFulfillmentMethod,
          bulkPreference: currentProduct?.bulkPreference,
          storeId: store?.storeId
        })
        : podFulFillmentUtils.getFulfillmentMethod(currentProduct);
      return {
        itemId: currentProduct?.itemId,
        quantity: currentProduct?.quantity,
        fulfillmentMethod: resolvedFulfillmentMethod,
        fulfillmentLocation: getFulfillmentLocation({
          fulfillmentMethod: resolvedFulfillmentMethod,
          store,
          product: currentProduct,
          deliveryZip: zipCode
        }),
        identifiers: currentProduct?.identifiers,
        pricing: currentProduct?.pricing
      };
    });

  const openDrawer = () => {
    if (isMobile && typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.mobile');
    }
    setDrawerShowing(true);
    quoteTimeTrackingRef.current.startTime = quoteStartTime ?? Math.floor(Date.now() / 1000);
  };

  const getAddToQuoteButton = () => {
    return renderAsLink
      ? (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        (<Link
          component="button"
          onClick={openDrawer}
          disabled={isDisabled}
          color={!isDisabled ? 'primary' : 'inactive'}
          data-testid="add-to-quote"
          data-component="AddToQuote"
        >
          { buttonText || 'Add to Quote' }
        </Link>)
      )
      : (
        <Button
          variant="secondary"
          fullWidth
          onClick={openDrawer}
          disabled={isDisabled}
          data-testid="add-to-quote"
          data-component="AddToQuote"
          ref={buttonRef}
        >
          { buttonText || 'Add to Quote' }
        </Button>
      );
  };

  return (
    <>
      {getAddToQuoteButton()}
      {drawerShowing && (
        <QueryProvider
          cacheKey="add-to-quote-drawer"
          defaultVariables={{ svocId: customerInfo?.svocId, storeId: store?.storeId }}
        >
          <AddToQuoteDrawer
            products={items}
            location={{
              localStoreId: store?.storeId,
              deliveryZip: zipCode,
              applianceDeliveryStore
            }}
            drawerShowing={drawerShowing}
            setDrawerShowing={setDrawerShowing}
            projectQuoteData={projectQuoteData}
            productExceptions={productExceptions}
            quoteTimeTrackingRef={quoteTimeTrackingRef}
            showNewATQUI={showNewATQUI}
          />
        </QueryProvider>
      )}
    </>
  );
};

const safeHTMLButtonPropType = typeof HTMLButtonElement !== 'undefined'
  ? PropTypes.instanceOf(HTMLButtonElement)
  : () => null;

AddToQuote.propTypes = {
  disabled: PropTypes.bool,
  fulfillmentLocation: PropTypes.string,
  fulfillmentMethod: PropTypes.string,
  itemId: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      fulfillment: PropTypes.shape({}).isRequired,
      identifiers: PropTypes.shape({}).isRequired,
      pricing: PropTypes.shape({}).isRequired,
      // Optional per-item fulfillment selection. When present it overrides the auto-pick:
      // selectedFulfillmentMethod is an explicit FULFILLMENT_METHODS value, bulkPreference a
      // category resolved against the item's own fulfillment data.
      selectedFulfillmentMethod: PropTypes.string,
      bulkPreference: PropTypes.oneOf(['pickup', 'delivery'])
    })
  ),
  productExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      flag: PropTypes.string.isRequired,
      fulfillment: PropTypes.shape({}),
      identifiers: PropTypes.shape({}),
      pricing: PropTypes.shape({}),
      itemId: PropTypes.string,
      id: PropTypes.string,
      quantity: PropTypes.number
    })
  ),
  quantity: PropTypes.number,
  buttonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: safeHTMLButtonPropType })
  ]),
  buttonText: PropTypes.string,
  projectQuoteData: PropTypes.shape({
    projectId: PropTypes.string,
    projectZipCode: PropTypes.string,
    projectName: PropTypes.string
  }),
  quoteStartTime: PropTypes.number,
  renderAsLink: PropTypes.bool,
  applianceDeliveryStore: PropTypes.string,
  enableApplianceAddToQuote: PropTypes.bool
};

AddToQuote.displayName = 'AddToQuote';

AddToQuote.dataModel = extend(dataModel, AddToQuoteDrawer.dataModel);

export { AddToQuote };
